import type {
  Listing,
  ModelOrganization,
  ModelUser,
  OrganizationOptionsInferred,
} from '~/models'

import { ModelStatus, UserGroup, UserRole } from '~/models'

type UserLike = Pick<ModelUser, '_id' | 'group' | 'role' | 'company'>

type CompanyLike = {
  _id: string
  tid?: string
  childCompanyIds?: string[]
}

type BrokerLike = {
  _id: string
}

export function isSuperAdmin(user: Pick<UserLike, 'group'> | null | undefined) {
  return user?.group === UserGroup.superAdmin
}

export function isOrganizationAdmin(
  user: Pick<UserLike, 'group'> | null | undefined
) {
  return user?.group === UserGroup.organizationAdmin
}

export function isOrganizationAdminOrSuperAdmin(
  user: Pick<UserLike, 'group'> | null | undefined
) {
  return isSuperAdmin(user) || isOrganizationAdmin(user)
}

export function isCompanyAdmin(
  user: Pick<UserLike, 'group'> | null | undefined
) {
  return user?.group === UserGroup.companyAdmin
}

export function isManagingBroker(
  user: Pick<UserLike, 'role'> | null | undefined
) {
  return user?.role === UserRole.managingBroker
}

export function isBroker(user: Pick<UserLike, 'role'> | null | undefined) {
  return user?.role === UserRole.broker
}

export function isCoordinator(user: Pick<UserLike, 'role'> | null | undefined) {
  return user?.role === UserRole.coordinator
}

export function isAppraiser(user: Pick<UserLike, 'role'> | null | undefined) {
  return user?.role === UserRole.appraiser
}

export function isBrokerOrManagingBroker(
  user: Pick<UserLike, 'role'> | null | undefined
) {
  return isBroker(user) || isManagingBroker(user)
}

export function hasChildCompanies(
  user: Pick<UserLike, 'company'> | null | undefined
) {
  return (
    Array.isArray(user?.company?.childCompanyIds) &&
    user.company.childCompanyIds.length > 0
  )
}

export function isActiveUserLike(user: unknown) {
  return !(
    user &&
    typeof user === 'object' &&
    'status' in user &&
    user.status === ModelStatus.inactive
  )
}

export function isSameUser(
  user: Pick<UserLike, '_id'> | null | undefined,
  otherUser: Pick<UserLike, '_id'> | null | undefined
) {
  return (
    !!user && !!otherUser && user._id.toString() === otherUser._id.toString()
  )
}

export function isSameCompany(
  company: Pick<CompanyLike, '_id'> | null | undefined,
  otherCompany: Pick<CompanyLike, '_id'> | null | undefined
) {
  return (
    !!company &&
    !!otherCompany &&
    company._id.toString() === otherCompany._id.toString()
  )
}

export function userBelongsToCompany(
  user: Pick<UserLike, 'company'> | null | undefined,
  company: Pick<CompanyLike, '_id' | 'tid'> | null | undefined
) {
  if (!user?.company || !company) {
    return false
  }

  if (user.company.tid && user.company.tid === company.tid) {
    return true
  }

  return (
    isSameCompany(user.company, company) ||
    user.company.childCompanyIds?.includes(company._id.toString()) === true
  )
}

export function getUserCompanyIds(
  user: Pick<UserLike, 'company'> | null | undefined,
  options: { includeParent?: boolean } = {}
) {
  const { includeParent = true } = options
  const companyId = user?.company?._id

  if (!companyId) {
    return []
  }

  const childCompanyIds = user.company?.childCompanyIds ?? []

  return includeParent ? [...childCompanyIds, companyId] : childCompanyIds
}

export function userHasCrsDataAccess(
  user:
    | (Pick<UserLike, 'group'> & {
        options?: { hasCrsDataAccess?: boolean | string }
      })
    | null
    | undefined,
  organization: Pick<OrganizationOptionsInferred, 'options'> | null | undefined
) {
  return (
    organization?.options.crsDataEnabled === true &&
    (user?.group !== UserGroup.member || user?.options?.hasCrsDataAccess)
  )
}

export function isExternalOrganizationMember(
  user:
    | {
        externalOrganizationMembership?: Pick<ModelOrganization, 'slug'> | null
      }
    | null
    | undefined,
  organizationSlug: string
) {
  return user?.externalOrganizationMembership?.slug === organizationSlug
}

export function isMemberOfOrganization(
  user:
    | (Pick<ModelUser, 'organization'> & {
        externalOrganizationMembership?: Pick<ModelOrganization, 'slug'> | null
      })
    | null
    | undefined,
  organizationSlug: string
) {
  return (
    user?.organization.slug === organizationSlug ||
    isExternalOrganizationMember(user, organizationSlug)
  )
}

export function userIsListingBroker(
  user: Pick<UserLike, '_id'> | null | undefined,
  listing: Pick<Listing, 'brokers'> | null | undefined
) {
  return (
    !!user &&
    listing?.brokers.some((broker: BrokerLike) => isSameUser(broker, user)) ===
      true
  )
}
